import { api } from './client';
import { appVersion } from './appVersion';

/**
 * The home screen, as the server describes it.
 *
 * ---------------------------------------------------------------------------
 * THE BUILDER HAS EXISTED THE WHOLE TIME AND NOTHING READ IT
 * ---------------------------------------------------------------------------
 * `ui_configs` on the server is a complete remote-configuration system:
 * versioned trees, draft → published → rollback, per city, per customer
 * segment, a `minAppVersion` gate, a validator that checks every category slug
 * and service id exists, and an admin editor at "App screens".
 *
 * No app has ever asked for it. `GET /ui-config/home` had zero callers, and
 * this screen's banners came from `heroBanners` in `data/services.ts` with the
 * artwork bundled into the binary - so an admin could compose a home screen,
 * publish it, and watch nothing happen. That is the worst state for a feature
 * to be in: built, reachable, and silently ignored.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS DOES NOT DO
 * ---------------------------------------------------------------------------
 * It does not decide anything. A tree that does not arrive, arrives malformed,
 * or arrives empty leaves the screen rendering exactly what it renders today -
 * see `FALLBACK_TREE` and the note on it. Remote configuration must never be
 * able to produce a blank home screen, because there is no way for a customer
 * to recover from one.
 */

/** A tap target. Only `type` and `target`; the server rejects anything else. */
export type UiAction = { type: string; target: string };

export type UiComponent =
  | {
      type: 'banner_carousel';
      id: string;
      items: Array<{
        imageUrl: string;
        accessibilityLabel?: string;
        action?: UiAction;
        /*
         * The overlay copy, drawn over the artwork by the app.
         *
         * Optional, because a banner may carry its words either way: printed
         * into the picture, or written here. `headline` is what switches the
         * overlay on - an offer whose number changes should be editable
         * without a designer and a re-upload, and one whose artwork already
         * says everything should not get a second set of words on top of it.
         */
        eyebrow?: string;
        headline?: string;
        cta?: string;
      }>;
    }
  | {
      type: 'category_grid';
      id: string;
      title?: string;
      categorySlugs: string[];
      columns?: number;
    }
  | {
      type: 'service_carousel';
      id: string;
      title?: string;
      serviceIds: string[];
    }
  | { type: 'text_block'; id: string; text: string; style?: string }
  | { type: 'spacer'; id: string; size?: string };

export type UiTree = {
  schemaVersion: number;
  components: UiComponent[];
};

export type ResolvedUiConfig = {
  /** Null when nothing is published for this city and segment. */
  jsonTree: UiTree | null;
  version?: number;
  schemaVersion?: number;
};

/**
 * What the screen draws when the server has said nothing usable.
 *
 * ---------------------------------------------------------------------------
 * NOT AN EMPTY TREE, AND NOT A GUESS
 * ---------------------------------------------------------------------------
 * `null` here means "render the screen you already had" rather than "render
 * nothing". The distinction is the whole safety story: no publication, a
 * network failure, a tree this build is too old for, or a tree that fails to
 * parse all resolve to the same thing, and that thing is the home screen that
 * shipped in the binary.
 *
 * An empty `components: []` from the server is treated the same way. It is
 * indistinguishable from a mistake in the editor, and the cost of guessing
 * wrong is a customer staring at a blank page with no way to reach the
 * catalogue.
 */
export const NO_TREE: UiTree | null = null;

/**
 * The part of a publication that speaks to one section of a screen.
 *
 * ---------------------------------------------------------------------------
 * A PUBLICATION SUPPLEMENTS A SCREEN, IT DOES NOT REPLACE IT
 * ---------------------------------------------------------------------------
 * Home used to render the whole tree INSTEAD of every compiled section it has.
 * So a tree was all or nothing: publishing one category block - which reads as
 * "I want to choose the categories" - deleted Popular Services from the page,
 * because the tree happened not to mention it. Ops could not change one part
 * of a screen without knowing every other part of it.
 *
 * Each section asks this for its own block types, and draws its compiled self
 * when the answer is empty. Banners already worked this way; this is that rule
 * written down and applied to the rest.
 *
 * `null` rather than an empty tree, because "nothing published for this
 * section" and "published, but nothing usable in it" are the same instruction
 * to the caller and deserve one branch, not two.
 */
export const sectionOf = (
  tree: UiTree | null | undefined,
  kinds: readonly string[],
): UiTree | null => {
  if (!tree) return NO_TREE;

  const picked = tree.components.filter(component =>
    kinds.includes(component.type),
  );

  return picked.length ? { ...tree, components: picked } : NO_TREE;
};

/**
 * Ask what the home screen should be.
 *
 * `X-App-Version` is required by the route: the server refuses to serve a
 * publication whose `minAppVersion` is above this build, which is what stops
 * an editor using a component type an old binary cannot draw. Sending the
 * wrong value would either hide a valid publication or hand this build
 * something it cannot render, so it comes from the same constant the update
 * gate uses rather than being retyped here.
 *
 * Signed in or not - the route takes an optional token and segments the answer
 * from it, falling back to `anonymous`. It is deliberately NOT sent
 * `anonymous: true`, because a signed-in customer should get the segment they
 * belong to.
 */
export const fetchHomeConfig = async (
  cityId?: string,
  signal?: AbortSignal,
): Promise<UiTree | null> => {
  const query = cityId ? `?cityId=${encodeURIComponent(cityId)}` : '';

  try {
    const resolved = await api.get<ResolvedUiConfig>(
      `/ui-config/home${query}`,
      { signal, headers: { 'X-App-Version': appVersion() } },
    );
    return usableTree(resolved?.jsonTree);
  } catch {
    /*
     * Swallowed, like the config probe. The screen has a home to draw without
     * this, and a launch that showed an error because a layout request timed
     * out would be a worse bug than the one remote configuration solves.
     */
    return NO_TREE;
  }
};

/**
 * A tree worth rendering, or null.
 *
 * Guards the two shapes that would otherwise reach the renderer and produce a
 * blank screen: a payload that is not a tree at all, and a tree with nothing
 * in it. Unknown component types are NOT filtered here - the renderer skips
 * what it cannot draw, which keeps "this build is too old for that block" a
 * per-block decision rather than an all-or-nothing one.
 */
export const usableTree = (tree: unknown): UiTree | null => {
  if (!tree || typeof tree !== 'object') return NO_TREE;

  const candidate = tree as Partial<UiTree>;
  if (!Array.isArray(candidate.components)) return NO_TREE;
  if (candidate.components.length === 0) return NO_TREE;

  return {
    schemaVersion: Number(candidate.schemaVersion) || 1,
    components: candidate.components.filter(
      (component): component is UiComponent =>
        !!component &&
        typeof component === 'object' &&
        typeof (component as UiComponent).type === 'string',
    ),
  };
};
